import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import DirectionSnackbar from '../../Components/Snackbar'
import { makeGetRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'

/**
 * Détection des virements internes (compte à compte) avec aperçu.
 * Affiche les paires débit/crédit appariées ; l'utilisateur valide avant le tag
 * « Interne ». Expose handleClickOpen() via ref (même contrat que les autres modales).
 */
const InternalTransfersModal = React.forwardRef((props, ref) => {
  const { onComplete } = props
  const { t } = useTranslation('layout')
  const tr = (k, dflt) => t(`finance.internalTransfers.${k}`, dflt)

  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [applying, setApplying] = React.useState(false)
  const [pairs, setPairs] = React.useState([])
  const [selected, setSelected] = React.useState(() => new Set())
  const [snack, setSnack] = React.useState({ open: false, color: 'info', message: '' })

  const showSnack = (color, message) => {
    setSnack({ open: true, color, message })
    setTimeout(() => setSnack({ open: false, color, message: '' }), 3500)
  }

  const detect = async () => {
    setLoading(true)
    const { data, status } = await makeGetRequest(urls.financeInternalTransfersDetect, {}, true)
    if (status === 200) {
      setPairs(data.pairs || [])
      setSelected(new Set((data.pairs || []).map((_, i) => i)))
    }
    setLoading(false)
  }

  const handleClickOpen = () => {
    setOpen(true)
    setPairs([])
    detect()
  }
  const handleClose = () => {
    if (applying) return
    setOpen(false)
  }
  React.useImperativeHandle(ref, () => ({ handleClickOpen }))

  const toggle = (i) =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(i)) n.delete(i)
      else n.add(i)
      return n
    })

  const apply = async () => {
    const chosen = pairs.filter((_, i) => selected.has(i))
    if (chosen.length === 0) return
    setApplying(true)
    const { data, status } = await makePostRequest(
      urls.financeInternalTransfersApply,
      { pairs: chosen },
      {},
      true,
    )
    setApplying(false)
    if (status === 200) {
      showSnack('success', tr('done', '{{n}} opérations taguées « Interne ».').replace('{{n}}', data.tagged || 0))
      if (typeof onComplete === 'function') onComplete()
      setTimeout(() => setOpen(false), 1200)
    } else {
      showSnack('error', (data && data.detail) || tr('error', 'Une erreur est survenue.'))
    }
  }

  return (
    <>
      {snack.open && <DirectionSnackbar message={snack.message} color={snack.color} />}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon fontSize="small" />
          {tr('title', 'Virements internes')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {tr('subtitle', 'Paires débit/crédit de même montant entre deux de vos comptes. Validez pour les exclure du compte de résultat.')}
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : pairs.length === 0 ? (
            <Alert severity="info">{tr('none', 'Aucun virement interne détecté.')}</Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 380 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.size === pairs.length}
                        indeterminate={selected.size > 0 && selected.size < pairs.length}
                        onChange={(e) => setSelected(e.target.checked ? new Set(pairs.map((_, i) => i)) : new Set())}
                      />
                    </TableCell>
                    <TableCell>{tr('date', 'Date')}</TableCell>
                    <TableCell>{tr('debit', 'Débit (sortie)')}</TableCell>
                    <TableCell>{tr('credit', 'Crédit (entrée)')}</TableCell>
                    <TableCell align="right">{tr('amount', 'Montant')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pairs.map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(i)} onChange={() => toggle(i)} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{p.debit.date}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={p.debit.libelle}>{p.debit.libelle}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.debit.account}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={p.credit.libelle}>{p.credit.libelle}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.credit.account}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {Math.abs(p.debit.montant).toLocaleString('fr-CH', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={applying}>{tr('cancel', 'Annuler')}</Button>
          <Button
            variant="contained"
            onClick={apply}
            disabled={applying || loading || selected.size === 0}
            startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
          >
            {tr('apply', 'Taguer {{n}} paire(s)').replace('{{n}}', selected.size)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
})

InternalTransfersModal.displayName = 'InternalTransfersModal'
export default InternalTransfersModal
